import { FC, useMemo, useState } from 'react';
import { AppId, usePlatformContext } from '@datassential/platform-ui-lib';
import AccessLink from '@components/AccessLink/AccessLink';
import HomeSectionFooter from '@pages/Home/components/HomeSectionFooter';
import { useIndependentsSavedSearches, SavedSearchItem } from '@apiHooks/useIndependentsSavedSearches';
import {
    SALES_TARGETING_TITLE,
    SAVED_SEARCHES_LABEL,
    START_NEW_SEARCH_LABEL,
    SAVED_SEARCHES_PERSONAL_LABEL,
    SAVED_SEARCHES_COMPANY_LABEL,
    SAVED_SEARCHES_PAGE_SIZE,
    START_NEW_SEARCH_PATH,
} from '../HomeSalesFocusChains.constants';
import './SalesFocusIndependentsSavedSearches.scss';

interface GroupProps {
    label: string;
    items: SavedSearchItem[];
}

const Group: FC<GroupProps> = ({ label, items }) => {
    const [expanded, setExpanded] = useState(false);

    if (items.length === 0) return null;

    const showFooter = items.length > SAVED_SEARCHES_PAGE_SIZE;
    const visible = expanded ? items : items.slice(0, SAVED_SEARCHES_PAGE_SIZE);
    const n = visible.length;
    const cut1 = Math.ceil(n / 3);
    const cut2 = Math.ceil((2 * n) / 3);
    const cols = [visible.slice(0, cut1), visible.slice(cut1, cut2), visible.slice(cut2)];

    return (
        <div className="SalesFocusIndependentsSavedSearches__group">
            <div className="SalesFocusIndependentsSavedSearches__group-label">{label}</div>
            <div className="SalesFocusIndependentsSavedSearches__links">
                {cols.map((col, colIdx) => (
                    <div key={colIdx} className="SalesFocusIndependentsSavedSearches__col">
                        {col.map((item, itemIdx) => (
                            <a
                                key={item.link ?? item.name ?? `${colIdx}-${itemIdx}`}
                                href={item.link ?? undefined}
                                target="_blank"
                                rel="noreferrer"
                                className="SalesFocusIndependentsSavedSearches__link"
                            >
                                {item.name}
                            </a>
                        ))}
                    </div>
                ))}
            </div>
            {showFooter && (
                <HomeSectionFooter className="SalesFocusIndependentsSavedSearches__footer">
                    <AccessLink
                        url={null}
                        hideArrowIcon
                        text={`Show ${expanded ? 'Less' : 'More'}`}
                        hasAccess={true}
                        onClick={() => setExpanded((prev) => !prev)}
                        onContactUsClick={() => {}}
                    />
                </HomeSectionFooter>
            )}
        </div>
    );
};

const SalesFocusIndependentsSavedSearches: FC = () => {
    const { data: { personal, companyOwned }, isLoading, isSuccess } = useIndependentsSavedSearches();
    const { userInfo: { products } } = usePlatformContext();
    const operatorsBaseUrl = useMemo(
        () => (products[AppId.Operators]?.url ?? '').replace(/\/+$/, ''),
        [products],
    );

    if (isLoading || isSuccess === false || (personal.length === 0 && companyOwned.length === 0)) return null;

    return (
        <div className="SalesFocusIndependentsSavedSearches">
            <div className="SalesFocusIndependentsSavedSearches__title">{SALES_TARGETING_TITLE}</div>
            <div className="SalesFocusIndependentsSavedSearches__header">
                <span className="SalesFocusIndependentsSavedSearches__header-label">
                    {SAVED_SEARCHES_LABEL}
                </span>
                <span>
                    {'or '}
                    <a
                        href={`${operatorsBaseUrl}${START_NEW_SEARCH_PATH}`}
                        target="_blank"
                        rel="noreferrer"
                        className="SalesFocusIndependentsSavedSearches__new-search-link"
                    >
                        {START_NEW_SEARCH_LABEL}
                    </a>
                </span>
            </div>
            <div className="SalesFocusIndependentsSavedSearches__body">
                <Group label={SAVED_SEARCHES_PERSONAL_LABEL} items={personal} />
                <Group label={SAVED_SEARCHES_COMPANY_LABEL} items={companyOwned} />
            </div>
        </div>
    );
};

export default SalesFocusIndependentsSavedSearches;
